import React, { useEffect, useState } from 'react';
import { alertsAPI, playersAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MessageSquare, Send, Users, AlertCircle, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const [message, setMessage] = useState('');
  const [recipientType, setRecipientType] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [alertsRes, playersRes] = await Promise.all([
        alertsAPI.getAll(),
        playersAPI.getAll()
      ]);
      setAlerts(alertsRes.data);
      setPlayers(playersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const response = await alertsAPI.send({ message, recipient_type: recipientType });
      const result = response.data;
      
      if (result.status === 'sent') {
        toast.success(`Alert sent to ${result.sent_count} recipients`);
      } else if (result.status === 'partial') {
        toast.warning(`Alert sent to ${result.sent_count}/${result.total_recipients} (${result.failed_count} failed)`);
      } else if (result.status === 'failed') {
        toast.error(`Failed to send alert (${result.failed_count} failed). Check phone numbers.`);
      } else if (result.status === 'mocked') {
        toast.success('Alert logged (Twilio not configured)');
      }
      
      setMessage('');
      loadData();
    } catch (error) {
      toast.error('Failed to send alert');
    } finally {
      setSending(false);
    }
  };

  const getRecipientIcon = (type) => {
    switch (type) {
      case 'all': return <Users className="w-4 h-4" />;
      case 'players': return <Users className="w-4 h-4" />;
      case 'parents': return <Users className="w-4 h-4" />;
      case 'injured': return <Heart className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getRecipientLabel = (type) => {
    switch (type) {
      case 'all': return 'All Members & Parents';
      case 'players': return 'Players Only';
      case 'parents': return 'Parents Only';
      case 'injured': return 'Injured Players & Parents';
      default: return type;
    }
  };

  const getRecipientCount = (type) => {
    switch (type) {
      case 'all': return players.length * 2; // players + parents
      case 'players': return players.length;
      case 'parents': return players.filter(p => p.parent_phone).length;
      case 'injured': return players.filter(p => p.status === 'injured' || p.status === 'recovering').length;
      default: return 0;
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <p className="overline">Communication</p>
          <h1 className="font-display text-4xl lg:text-5xl text-white mt-2">TEXT ALERTS</h1>
        </div>

        {/* Twilio Status Notice */}
        <div className="flex items-start gap-3 p-4 bg-[#32D74B]/10 border border-[#32D74B]/30">
          <MessageSquare className="w-5 h-5 text-[#32D74B] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[#32D74B] font-medium text-sm">Twilio SMS Enabled</p>
            <p className="text-[#A1A1AA] text-sm mt-1">
              Text alerts are sent via Twilio. Messages will be delivered to verified phone numbers.
              Trial accounts require phone verification at twilio.com.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Compose Message */}
          <Card className="bg-[#121212] border-[#27272A]" data-testid="compose-alert">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="flex items-center gap-2 text-white">
                <MessageSquare className="w-5 h-5" />
                Compose Alert
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="text-xs text-[#71717A] uppercase tracking-wider block mb-2">Recipients</label>
                <Select value={recipientType} onValueChange={setRecipientType}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white" data-testid="recipient-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    <SelectItem value="all" className="text-white hover:bg-[#27272A]">
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" /> All Members & Parents
                      </span>
                    </SelectItem>
                    <SelectItem value="players" className="text-white hover:bg-[#27272A]">
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" /> Players Only
                      </span>
                    </SelectItem>
                    <SelectItem value="parents" className="text-white hover:bg-[#27272A]">
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" /> Parents Only
                      </span>
                    </SelectItem>
                    <SelectItem value="injured" className="text-white hover:bg-[#27272A]">
                      <span className="flex items-center gap-2">
                        <Heart className="w-4 h-4" /> Injured Players & Parents
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#71717A] mt-2">
                  ~{getRecipientCount(recipientType)} recipients
                </p>
              </div>

              <div>
                <label className="text-xs text-[#71717A] uppercase tracking-wider block mb-2">Message</label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message here..."
                  className="bg-[#0A0A0A] border-[#27272A] text-white min-h-[150px]"
                  data-testid="alert-message-input"
                />
                <p className="text-xs text-[#71717A] mt-2 text-right">
                  {message.length} characters
                </p>
              </div>

              <Button
                onClick={handleSend}
                disabled={sending || !message.trim()}
                className="w-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white btn-press"
                data-testid="send-alert-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sending...' : 'Send Alert'}
              </Button>
            </CardContent>
          </Card>

          {/* Recent Alerts */}
          <Card className="bg-[#121212] border-[#27272A]" data-testid="recent-alerts-list">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="flex items-center gap-2 text-white">
                <AlertCircle className="w-5 h-5" />
                Recent Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-[#71717A]">Loading...</div>
                </div>
              ) : alerts.length > 0 ? (
                <div className="divide-y divide-[#27272A] max-h-[500px] overflow-y-auto">
                  {alerts.map((alert) => (
                    <div 
                      key={alert.id} 
                      className="p-4 hover:bg-[#1A1A1A] transition-colors"
                      data-testid={`alert-item-${alert.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 text-[#71717A]">
                          {getRecipientIcon(alert.recipient_type)}
                          <span className="text-xs">{getRecipientLabel(alert.recipient_type)}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 border ${
                          alert.status === 'sent' ? 'text-[#32D74B] bg-[#32D74B]/10 border-[#32D74B]/30' :
                          alert.status === 'partial' ? 'text-[#FFD60A] bg-[#FFD60A]/10 border-[#FFD60A]/30' :
                          alert.status === 'failed' ? 'text-[#FF3B30] bg-[#FF3B30]/10 border-[#FF3B30]/30' :
                          'text-[#71717A] bg-[#71717A]/10 border-[#71717A]/30'
                        }`}>
                          {alert.status === 'sent' ? 'SENT' :
                           alert.status === 'partial' ? 'PARTIAL' :
                           alert.status === 'failed' ? 'FAILED' :
                           'LOGGED'}
                        </span>
                      </div>
                      <p className="text-white text-sm">{alert.message}</p>
                      <div className="flex items-center justify-between mt-3 text-xs text-[#71717A]">
                        <span>
                          Sent by {alert.sent_by}
                          {alert.total_recipients > 0 && (
                            <span className="ml-2">
                              • {alert.sent_count || 0}/{alert.total_recipients} delivered
                            </span>
                          )}
                        </span>
                        <span>{formatDateTime(alert.sent_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                  <MessageSquare className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg">No alerts sent yet</p>
                  <p className="text-sm">Compose your first message above</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Templates */}
        <Card className="bg-[#121212] border-[#27272A]">
          <CardHeader className="border-b border-[#27272A]">
            <CardTitle className="text-white">Quick Templates</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Practice Reminder', text: 'Reminder: Practice tomorrow at 4:00 PM. Please arrive 15 minutes early.' },
                { label: 'Practice Cancelled', text: 'Practice has been cancelled today due to weather conditions. Stay safe!' },
                { label: 'Game Day', text: 'Game day! Players report at 5:00 PM. Parents, gates open at 6:00 PM.' },
                { label: 'Meeting Notice', text: 'Team meeting scheduled for this week. Check your email for details.' }
              ].map((template, idx) => (
                <button
                  key={idx}
                  onClick={() => setMessage(template.text)}
                  className="p-4 bg-[#0A0A0A] border border-[#27272A] hover:border-[#3F3F46] text-left transition-colors"
                  data-testid={`template-${idx}`}
                >
                  <p className="text-white font-medium text-sm mb-1">{template.label}</p>
                  <p className="text-[#71717A] text-xs line-clamp-2">{template.text}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
